import {ArmaDate, doArrayFlat} from '../../classes/utilit';

export type GetMatchedVariableFromArray<T> = (array: T[keyof T]) => string | undefined;

/**
 * Types de filtre
 */
export enum FilterType {
  Checkbox,
  Datebox // La date doit etre en unix number
}

/**
 * Definition des colone à gerer pour le TableFilter
 */
export interface IColumnFilter<T> {
  /** Nom du MAT_COLUMN a filtrer */
  columnName: string;
  /** Type de filtre à appliquer */
  filterType: FilterType;
  /** variable de l'inerface à utilisé pour le filtre */
  matchedVariable: keyof T;

  isVariableArray?: GetMatchedVariableFromArray<T>;
}

/**
 * Classe parente générique pour une colonne filtrée
 */
export abstract class ColumnFilter<T> implements IColumnFilter<T> {

  public columnName: string;
  public filterType: FilterType;
  public matchedVariable: keyof T; // Ce string correspond au nom de la variable du contrat (classe Order) testée
  public isVariableArray?: GetMatchedVariableFromArray<T>;

  protected constructor(columnFilter: IColumnFilter<T>) {
    this.columnName = columnFilter.columnName;
    this.filterType = columnFilter.filterType;
    this.matchedVariable = columnFilter.matchedVariable;
    this.isVariableArray = columnFilter.isVariableArray;
  }

  // Fonction de test renvoit faux quand la valeur passée ne correspond pas aux filtres
  abstract match(value: any | T[keyof T]): boolean;
}

/**
 * Colonne filtrée avec des checkbox par valeur distinctes dans la colonne
 */
export class ColumnFilterChecklist<T> extends ColumnFilter<T> {

  filters: FilterCheckboxItem[] = [];

  constructor(columnFilter: IColumnFilter<T>) {
    super(columnFilter);
  }

  // Si toutes les checkbox sont cochées
  get allSelected(): boolean {
    // Faire un return dans forEach ne fait que sortir de la boucle
    let resultSelected = true;
    this.filters.forEach(valueFilter => {
      if (!valueFilter.selected) {
        resultSelected = false;
      }
    });
    return resultSelected;
  }

  // Permet de cocher toutes les checkbox de la colonne
  set allSelected(value: boolean) {
    this.filters.forEach(valueFilter => {
      valueFilter.selected = value;
    });
  }

  // Fonction de test
  match(value: any | T[keyof T]): boolean {
    if (this.allSelected)
      return true;
    if (this.isVariableArray)
      return this.matchArray(value);
    else
      return this.matchStr(value);
  }

  private matchArray(value: any[]): boolean {
    if (!this.isVariableArray)
      return false;
    const arrayFlat = doArrayFlat(value);
    for (let i = 0 ; i < arrayFlat.length ; i++) {
      for (let j = 0 ; j < this.filters.length ; j++) {
        const valueStr = this.isVariableArray(arrayFlat[i])?.toLowerCase() ?? '';
        const filterValueStr = this.filters[j].filterValue?.toLowerCase() ?? '';
        if (valueStr == filterValueStr && this.filters[j].selected)
          return true;
      }
    }
    return false;
  }

  private matchStr(value: string): boolean {
    let resultMatch = true;
    this.filters.forEach(filterValue => {
      const valueStr = value ?? '';
      const filetValueStr = filterValue.filterValue ?? '';
      // Si le filtre = la valeur testée
      if (filetValueStr.toLowerCase() == valueStr.toLowerCase()) {
        resultMatch = filterValue.selected;
        if (!resultMatch)
          return;
      }
    });
    // Renvoit vrai par défaut
    return resultMatch;
  }
}

/**
 * Colonne filtrée par date (Date de début + Date de fin)
 */
export class ColumnFilterDate<T> extends ColumnFilter<T> {

  beginDate: FilterDateboxItem;
  endDate: FilterDateboxItem;

  constructor(columnFilter: IColumnFilter<T>,
              beginDate: FilterDateboxItem,
              endDate: FilterDateboxItem) {
    super(columnFilter);
    this.beginDate = beginDate;
    this.endDate = endDate;
  }

  /**
   * If the date is between the begin and end dates, return true. Otherwise, return false
   * @param unixValue - number
   * @returns The return value is a boolean.
   */
  match(unixValue: number): boolean {
    const dateValue: ArmaDate = ArmaDate.createFromUnixTime(unixValue);
    if (this.beginDate.date != null) {
      if (dateValue.getClonnedDate() < this.beginDate.date) return false;
    }
    if (this.endDate.date != null) {
      if (dateValue.getClonnedDate() > this.endDate.date) return false;
    }
    return true;
  }

  // Renvoit vrai si au moins une filtre a été saisi
  get isFiltering(): boolean {
    return (this.beginDate.date != undefined) || (this.endDate.date != undefined);
  }
}

/**
 * Classe parente générique pour chaque filtre
 */
export abstract class FilterItem {

  columnName: string;

  protected constructor(column: string) {
    this.columnName = column;
  }
}

/**
 * Filtre de type checkbox : Contient le texte du checkbox et si il est coché ou non
 */
export class FilterCheckboxItem extends FilterItem {

  filterValue: string;
  selected: boolean;

  constructor(column: string,
              value: string,
              isSelected: boolean) {
    super(column);
    this.filterValue = value;
    this.selected = isSelected;
  }
}

/**
 * Filtre de type armaDateTimePicker : Contient la date sélectionnée
 */
export class FilterDateboxItem extends FilterItem {
  constructor(column: string) {
    super(column);
    //this.date = new Date(); // Pour init a vide input type date
  }
  date?: Date;
}

/**
 * Classe qui gère le filtre sur le tableau entier
 * Prend en compte le filtre sur les différentes collones et aussi le filtrer texte global
 * <p>
 * How to use:
 * <ul>
 *  <li>1- Definir les colone a filter a l'aide de l'interface {@see IColumnFilter}</li>
 *  <li>2- Instancier la class dans le constructor du composent</li>
 *  <li>3- Dans le constructeur: overide le filterPredicate du datasource sur la fonction match du tableFilter
 *  (EX: this.HTML_TableDataSource.filterPredicate = data => this.mTableFilter.match(data);)</li>
 *  <li>4- A la fin du refresh des donnée: appeler la fonction : generateColumnFiltersItemFromArray</li>
 *  <li>5- Crée une fonction qui va placer Date.now().toString() sur le filter du datasource pour trigger a chaque modification le tablefilter</li>
 *  <li>6 - Placer les composent sur le html</li>
 *  <li>7 - Si on souhaite aussi filtrer avec un input il faut ngmodel la variable {@see TableFilter.globalFilter}</li>
 * </ul>
 * </p>
 */
export class TableFilter<T> {

  /**
   * Filtre général sur toutes les colonnes du tableau
   */
  public globalFilter: string = '';

  /**
   * Liste des colonnes avec filtre
   */
  private readonly columnFilters: ColumnFilter<T>[] = [];

  constructor(arrayColumnFilter: IColumnFilter<T>[]) {
    // Création des colonnes de tri passées
    arrayColumnFilter.forEach(columnFilter => {
      switch (columnFilter.filterType) {
        case FilterType.Checkbox:
          this.columnFilters.push(new ColumnFilterChecklist(columnFilter));
          break;
        case FilterType.Datebox:
          this.columnFilters.push(new ColumnFilterDate(
            columnFilter,
            new FilterDateboxItem(columnFilter.columnName),
            new FilterDateboxItem(columnFilter.columnName)
          ));
          break;
      }
    });
  }

  /**
   * Test les données avec les filtres de chaque colonne + filtre général
   * @param data ?
   * @return true si on affiche la ligne dans le tableau, sinon false
   */
  match(data: any | T): boolean {
    // Filtre par colonne
    if (!this.matchColumnFilter(data))
      return false;
    // Filtre global : C'est le filtre par défaut de MatTable : On filtre sur toutes les colonnes
    return this.matchGlobalFilter(data);
  }

  matchColumnFilter(data: any | T): boolean {
    // Filtre par colonne
    for (let i = 0; i < this.columnFilters.length; i++) {
      const columnFilter: ColumnFilter<T> = this.columnFilters[i];
      if (!columnFilter.match(data[columnFilter.matchedVariable]))
        return false;
    }
    return true;
  }

  matchGlobalFilter(data: any | T) {
    // Filtre global : C'est le filtre par défaut de MatTable : On filtre sur toutes les colonnes
    return TableFilter.matchJson(data, this.globalFilter, 3);
  }

  public static matchJson(json: any, filterText: string, minFilterLength: number): boolean {
    try {
      if (filterText && filterText.length < minFilterLength)
        return true;
      const jsonString = JSON.stringify(json);
      if (jsonString.toLowerCase().indexOf(filterText.toLowerCase()) === -1)
        return false;
    } catch (error) {
      console.error(error);
    }
    return true;
  }

  /**
   * Récupère la colonne par son nom
   * @param column nom de la colone definit dans l'interface {@see IColumnFilter}
   */
  public getColumn(column: string): ColumnFilter<T> {
    // @ts-ignore TODO: TS2322: Type 'ColumnFilter<T> | undefined' is not assignable to type 'ColumnFilter<T>'
    return this.columnFilters.find(value => value.columnName == column);
  }

  /**
   * Renvoit la colonne avec le nom passé, casté en Colonne filtrée par date (Fait pour simplifier la partie HTML)
   * @param colName nom de la colone definit dans l'interface {@see IColumnFilter}
   */
  public getColumnFilterDate(colName: string): ColumnFilterDate<T> {
    const columnFilter = this.getColumn(colName);
    if (columnFilter instanceof ColumnFilterDate)
      return columnFilter;
    throw 'Not a column date';
  }

  /**
   * Renvoit la colonne avec le nom passé, casté en Colonne filtrée par checkbox (Fait pour simplifier la partie HTML)
   * @param colName nom de la colone definit dans l'interface {@see IColumnFilter}
   */
  public getColumnFilterChecklist(colName: string): ColumnFilterChecklist<T> {
    const columnFilter = this.getColumn(colName);
    if (columnFilter instanceof ColumnFilterChecklist)
      return columnFilter;
    throw 'Not a column checklist';
  }

  public generateColumnFiltersItemFromArray(arrayColumnInfo: IColumnFilter<T>[], tableDataSource: T[]) {
    arrayColumnInfo.forEach(columnInfo => this.generateColumnFiltersItem(columnInfo, tableDataSource));
  }

  /**
   * Génère la liste des colonnes
   * @param columnInfo ?
   * @param tableDataSource ?
   */
  public generateColumnFiltersItem(columnInfo: IColumnFilter<T>, tableDataSource: T[]) {
    switch (columnInfo.filterType) {
      case FilterType.Checkbox:
        this.generateCheckBoxItem(columnInfo, tableDataSource);
        break;
      case FilterType.Datebox:
        this.generateDateBoxItem(columnInfo.columnName);
        break;
      default:
        console.warn('Unknown filter type', columnInfo.filterType);
        break;
    }
  }

  private generateCheckBoxItem(columnInfo: IColumnFilter<T>, tableDataSource: T[]) {
    // Recuperation de la colone à traiter, création+insert si elle n'existe pas
    let filterColumnItem: ColumnFilterChecklist<T> = this.getColumnFilterChecklist(columnInfo.columnName);
    if (filterColumnItem == null) {
      filterColumnItem = new ColumnFilterChecklist(columnInfo);
      this.columnFilters.push(filterColumnItem);
    }
    // Raz du tableau
    filterColumnItem.filters.length = 0;
    // Recuperation des donnée
    const keyOfItem = columnInfo.matchedVariable;
    const distinctValues: string[] = [];
    // Si c'est un filtre par checkbox on doit récupérer les valeurs distinctes du tableau
    if (columnInfo.isVariableArray) { // Traitement pour un array
      // On recup le tableau qu'on flat pour obtenir une seule liste
      const mapValues = doArrayFlat(tableDataSource.map(item => item[keyOfItem]));
      // Set pour distinct, le string unique que l'on souhaite recupeer depuis le tableau
      // @ts-ignore TODO: TS2345: Argument of type 'string | undefined' is not assignable to parameter of type 'string'
      distinctValues.push(... new Set(mapValues.map(value => columnInfo.isVariableArray(value))));
    } else { // Traitement clasique
      // On recup la valeur que l'on souhaite traiter
      const mapValues = tableDataSource.map(item => item[keyOfItem] ? String(item[keyOfItem]) : '');
      // Set pour garder faire un distinct sur le tableau
      distinctValues.push(... new Set(mapValues));
    }
    // On crée les filtres de textes
    distinctValues.forEach(value => filterColumnItem.filters.push(new FilterCheckboxItem(columnInfo.columnName, value ? String(value) : '', true)));
  }

  private generateDateBoxItem(columnName: string) {
    // Date de création : Date de début, date de fin
    const filterBeginDate: FilterDateboxItem = new FilterDateboxItem(columnName);
    const filterEndDate: FilterDateboxItem = new FilterDateboxItem(columnName);
    const columnFilterDateCreation: ColumnFilterDate<T> = this.getColumn(columnName) as ColumnFilterDate<T>;
    columnFilterDateCreation.beginDate = filterBeginDate;
    columnFilterDateCreation.endDate = filterEndDate;
  }
}
